import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatMinorAmount } from '@/lib/commerce-format';
import { createIdempotencyKey } from '@/lib/idempotency-key';
import {
  type OrderListRecord,
  type OrderLine,
  useGetOrderQuery,
  useGetOrdersQuery,
  useGetOrderStatusQuery,
  useGetRelatedOrdersQuery,
  usePreviewRefundOrderMutation,
  useRefundOrderMutation,
} from '@/store/api/commerceP2cApi';

const orderStatuses = ['open', 'quoted', 'closed', 'partially_refunded', 'fully_refunded', 'refund_pending', 'cancelled'];

interface RelatedOrderNode {
  id: string;
  type?: string;
  status?: string;
  children: RelatedOrderNode[];
}

function toRelatedNode(value: unknown): RelatedOrderNode | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  if (typeof row.id !== 'string') return undefined;
  const children = Array.isArray(row.children)
    ? row.children.map(toRelatedNode).filter((node): node is RelatedOrderNode => Boolean(node))
    : [];
  return {
    id: row.id,
    ...(typeof row.type === 'string' ? { type: row.type } : {}),
    ...(typeof row.status === 'string' ? { status: row.status } : {}),
    children,
  };
}

function dateFilter(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00`).toISOString();
}

export function OrdersPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [storeId, setStoreId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<OrderListRecord[]>([]);
  const isDetail = Boolean(orderId);
  const filters = {
    cursor,
    limit: 25,
    status: status || undefined,
    search: search.trim() || undefined,
    storeId: storeId.trim() || undefined,
    workspaceId: workspaceId.trim() || undefined,
    dateFrom: dateFilter(dateFrom),
    dateTo: dateFilter(dateTo),
  };
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetOrdersQuery(filters, { skip: isDetail });
  const {
    data: order,
    isLoading: isLoadingOrder,
    isError: isOrderError,
    refetch: refetchOrder,
  } = useGetOrderQuery(orderId ?? '', { skip: !orderId });
  const {
    data: statusRecord,
    isError: isStatusError,
    refetch: refetchStatus,
  } = useGetOrderStatusQuery(orderId ?? '', { skip: !orderId });
  const {
    data: related,
    isLoading: isLoadingRelated,
    isError: isRelatedError,
    refetch: refetchRelated,
  } = useGetRelatedOrdersQuery(orderId ?? '', { skip: !orderId });

  useEffect(() => {
    setCursor(undefined);
    setRows([]);
  }, [search, status, storeId, workspaceId, dateFrom, dateTo]);

  useEffect(() => {
    if (!page) return;
    setRows((previous) => {
      if (!cursor) return page.data;
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  useEffect(() => {
    const initialOrderId = searchParams.get('orderId');
    if (initialOrderId && !orderId) setSearch(initialOrderId);
  }, [searchParams, orderId]);

  if (orderId) {
    const relatedRoot = toRelatedNode(related);
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild><Link to="/orders">← Orders</Link></Button>
        <header><h1 className="text-2xl font-bold">Order {order?.id ?? orderId}</h1><p className="text-sm text-muted-foreground">Inspect returned order data. Customer and store values are identifiers, not joined display names.</p></header>
        {isLoadingOrder ? <LoadingRows label="Loading order" columns={4} />
          : isOrderError || !order ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load this order. <Button variant="outline" onClick={() => void refetchOrder()}>Try again</Button></div>
            : <>
              <section className="grid gap-4 border-y py-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Order summary">
                <Detail label="Order ID" value={order.id} /><Detail label="Status" value={statusRecord?.status ?? order.status} />
                <Detail label="Created" value={formatDate(order.createdAt)} /><Detail label="Store ID" value={order.storeId} />
                <Detail label="Workspace ID" value={order.workspaceId} /><Detail label="Customer ID" value={order.customerId ?? '—'} />
                <Detail label="Channel" value={order.channel} /><Detail label="Total" value={order.totals?.total ? formatMinorAmount(order.totals.total.amount, order.totals.total.currency) : '—'} />
              </section>
              {isStatusError && <div role="alert" className="text-sm text-destructive">Could not refresh order status. <Button variant="outline" onClick={() => void refetchStatus()}>Try again</Button></div>}
              <section className="space-y-3">
                <h2 className="text-lg font-semibold">Line items</h2>
                {(order.lines ?? []).length === 0 ? <p className="text-sm text-muted-foreground">No line items returned.</p> : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Order line items"><table className="w-full min-w-[38rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Product / variant ID</th><th scope="col" className="px-4 py-3 text-right">Quantity</th><th scope="col" className="px-4 py-3 text-right">Unit price</th><th scope="col" className="px-4 py-3 text-right">Line net</th></tr></thead><tbody>{order.lines?.map((line) => <tr key={line.id} className="border-t"><td className="px-4 py-3">{line.productId ?? line.variantId ?? '—'}</td><td className="px-4 py-3 text-right tabular-nums">{line.quantity}</td><td className="px-4 py-3 text-right tabular-nums">{formatDecimalUnitPrice(line.unitPrice, order.currency)}</td><td className="px-4 py-3 text-right tabular-nums">{line.lineTotals?.net ? formatMinorAmount(line.lineTotals.net.amount, line.lineTotals.net.currency) : '—'}</td></tr>)}</tbody></table></div>}
              </section>
              {(statusRecord?.status ?? order.status) === 'closed' || (statusRecord?.status ?? order.status) === 'partially_refunded'
                ? <OrderRefundWorkflow orderId={order.id} orderStatus={statusRecord?.status ?? order.status} lines={order.lines ?? []} />
                : null}
              <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="text-lg font-semibold">Tender summary</h2><Button variant="outline" asChild><Link to={`/payments?orderId=${encodeURIComponent(order.id)}`}>View tender records</Link></Button></div>
                {(order.tenderSummary ?? []).length === 0 ? <p className="text-sm text-muted-foreground">No tender summary returned.</p> : <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Order tender summary"><table className="w-full min-w-[34rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Method</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3 text-right">Amount</th><th scope="col" className="px-4 py-3 text-right">Refunded</th></tr></thead><tbody>{order.tenderSummary.map((tender) => <tr key={tender.id} className="border-t"><td className="px-4 py-3">{tender.kind}</td><td className="px-4 py-3">{tender.status}</td><td className="px-4 py-3 text-right tabular-nums">{formatMinorAmount(tender.amount, tender.currency)}</td><td className="px-4 py-3 text-right tabular-nums">{formatMinorAmount(tender.refundedAmount, tender.currency)}</td></tr>)}</tbody></table></div>}
              </section>
              <section className="space-y-3"><h2 className="text-lg font-semibold">Related orders</h2>{isLoadingRelated ? <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading related orders…</p> : isRelatedError ? <div role="alert" className="text-sm text-destructive">Could not load related orders. <Button variant="outline" onClick={() => void refetchRelated()}>Try again</Button></div> : relatedRoot ? <><ul className="list-disc pl-5"><RelatedNode node={relatedRoot} /></ul>{related?.truncated === true && <p className="text-sm text-muted-foreground">The related-order tree was truncated by the service.</p>}</> : <p className="text-sm text-muted-foreground">No related-order tree returned.</p>}</section>
            </>}
      </div>
    );
  }

  const activeFilters = [
    search.trim() && `ID search: ${search.trim()}`,
    status && `Status: ${status}`,
    storeId.trim() && `Store ID: ${storeId.trim()}`,
    workspaceId.trim() && `Workspace ID: ${workspaceId.trim()}`,
    dateFrom && `From: ${dateFrom}`,
    dateTo && `To (exclusive): ${dateTo}`,
  ].filter((value): value is string => Boolean(value));
  const clearFilters = () => { setSearch(''); setStatus(''); setStoreId(''); setWorkspaceId(''); setDateFrom(''); setDateTo(''); };

  return (
    <div className="space-y-6">
      <header><h1 className="text-2xl font-bold">Orders</h1><p className="text-sm text-muted-foreground">Search matches order and customer IDs only; it does not join display names.</p></header>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Order or customer ID" id="order-search"><Input id="order-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Enter an ID" /></Field>
        <Field label="Status" id="order-status"><select id="order-status" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{orderStatuses.map((value) => <option key={value} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
        <Field label="Store ID" id="order-store"><Input id="order-store" value={storeId} onChange={(event) => setStoreId(event.target.value)} /></Field>
        <Field label="Workspace ID" id="order-workspace"><Input id="order-workspace" value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)} /></Field>
        <Field label="Created from (inclusive)" id="order-date-from"><Input id="order-date-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></Field>
        <Field label="Created to (exclusive)" id="order-date-to"><Input id="order-date-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></Field>
      </div>
      {activeFilters.length > 0 && <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">{activeFilters.map((filter) => <span key={filter} className="rounded-full border px-3 py-1 text-xs">{filter}</span>)}<Button variant="ghost" onClick={clearFilters}>Clear all</Button></div>}
      <section aria-busy={isLoading || isFetching}>
        {isLoading && rows.length === 0 ? <LoadingRows label="Loading orders" columns={6} />
          : isError && rows.length === 0 ? <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load orders. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>
            : rows.length === 0 ? <EmptyState title={activeFilters.length ? 'No matching orders' : 'No orders yet'} description={activeFilters.length ? 'Clear filters or adjust the IDs and date range.' : 'Orders returned for this organization will appear here.'} action={activeFilters.length ? <Button variant="outline" onClick={clearFilters}>Clear filters</Button> : undefined} />
              : <div className="space-y-3">{isError && <div role="alert" className="text-sm text-destructive">Could not refresh orders; loaded rows are retained. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>}<div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Orders list"><table className="w-full min-w-[54rem] text-sm"><thead className="bg-muted/50 text-left"><tr><th scope="col" className="px-4 py-3">Order ID</th><th scope="col" className="px-4 py-3">Status</th><th scope="col" className="px-4 py-3">Customer ID</th><th scope="col" className="px-4 py-3">Store ID</th><th scope="col" className="px-4 py-3">Channel</th><th scope="col" className="px-4 py-3 text-right">Total</th><th scope="col" className="px-4 py-3">Created</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id} className="border-t"><td className="px-4 py-3"><Link className="font-medium text-primary underline-offset-4 hover:underline" to={`/orders/${row.id}`}>{row.id}</Link></td><td className="px-4 py-3">{row.status}</td><td className="px-4 py-3">{row.customerId ?? '—'}</td><td className="px-4 py-3">{row.storeId}</td><td className="px-4 py-3">{row.channel}</td><td className="px-4 py-3 text-right tabular-nums">{row.totals?.total ? formatMinorAmount(row.totals.total.amount, row.totals.total.currency) : '—'}</td><td className="px-4 py-3">{formatDate(row.createdAt)}</td></tr>)}</tbody></table></div><p className="text-sm text-muted-foreground">Loaded {rows.length} orders{page?.nextCursor ? '; more results are available.' : ''}</p>{page?.nextCursor && <Button variant="outline" disabled={isFetching} onClick={() => setCursor(page.nextCursor ?? undefined)}>{isFetching ? 'Loading…' : 'Load more orders'}</Button>}</div>}
      </section>
    </div>
  );
}

function OrderRefundWorkflow({ orderId, orderStatus, lines }: { orderId: string; orderStatus: string; lines: OrderLine[] }) {
  const [previewRefund, { isLoading: isPreviewLoading }] = usePreviewRefundOrderMutation();
  const [submitRefund, { isLoading }] = useRefundOrderMutation();
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [refundPreview, setRefundPreview] = useState<{ amountMinor: string; currency: string } | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const selectedLines = lines.filter((line) => selectedLineIds.includes(line.id));
  const validLines = selectedLines.filter((line) => isValidRefundQuantity(quantities[line.id] ?? '', line.quantity));
  const selectionIsValid = selectedLines.length > 0 && validLines.length === selectedLines.length;

  function toggleLine(line: OrderLine, checked: boolean) {
    setSelectedLineIds((current) => checked ? [...current, line.id] : current.filter((id) => id !== line.id));
    if (checked) setQuantities((current) => ({ ...current, [line.id]: current[line.id] ?? line.quantity }));
    setConfirmationOpen(false);
    setRefundPreview(null);
    setIdempotencyKey(null);
    setErrorMessage(null);
    setSuccessMessage(null);
  }

  async function confirmRefund() {
    if (!selectionIsValid || validLines.length !== selectedLines.length || !idempotencyKey || !refundPreview) return;
    setErrorMessage(null);
    try {
      const result = await submitRefund({
        orderId,
        lines: validLines.map((line) => ({ originalOrderLineId: line.id, quantity: quantities[line.id]! })),
        idempotencyKey,
      }).unwrap();
      const settlementStatus = result.settlement?.status ?? result.status;
      setSuccessMessage(settlementStatus === 'closed'
        ? 'Refund completed successfully.'
        : 'Refund submitted; settlement is pending.');
      setSelectedLineIds([]);
      setConfirmationOpen(false);
      setRefundPreview(null);
      setIdempotencyKey(null);
    } catch (error) {
      const code = apiErrorCode(error);
      setErrorMessage(code
        ? `Refund was not accepted (${code}). Refund eligibility is determined by the order service.`
        : 'Refund could not be submitted. The order service determines eligibility; retry when ready.');
    }
  }

  async function reviewRefund() {
    if (!selectionIsValid || validLines.length !== selectedLines.length) return;
    setErrorMessage(null);
    setSuccessMessage(null);
    setRefundPreview(null);
    let key: string;
    try {
      key = createIdempotencyKey();
    } catch {
      setErrorMessage('Refund cannot be reviewed because secure random generation is unavailable.');
      return;
    }
    setIdempotencyKey(key);
    try {
      const preview = await previewRefund({
        orderId,
        lines: validLines.map((line) => ({ originalOrderLineId: line.id, quantity: quantities[line.id]! })),
      }).unwrap();
      setRefundPreview({ amountMinor: preview.amountMinor, currency: preview.currency });
      setConfirmationOpen(true);
    } catch {
      setIdempotencyKey(null);
      setErrorMessage('Refund preview could not be loaded. The order service must confirm the amount before submission.');
    }
  }

  return (
    <section className="space-y-4 rounded-md border p-4" aria-label="Refund order">
      <div>
        <h2 className="text-lg font-semibold">Refund</h2>
        <p className="text-sm text-muted-foreground">Select order lines and quantities. The order service checks refund eligibility and remaining refundable quantities.</p>
      </div>
      {lines.length === 0 ? <p className="text-sm text-muted-foreground">No order lines are available to select.</p> : (
        <div className="space-y-3">
          {lines.map((line) => {
            const checked = selectedLineIds.includes(line.id);
            const itemName = line.productId ?? line.variantId ?? line.id;
            const quantity = quantities[line.id] ?? line.quantity;
            const quantityIsValid = isValidRefundQuantity(quantity, line.quantity);
            return <div key={line.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_12rem] sm:items-end">
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input type="checkbox" checked={checked} disabled={isLoading || isPreviewLoading} onChange={(event) => toggleLine(line, event.target.checked)} aria-label={`Select ${itemName} for refund`} />
                <span>{itemName} · sold quantity {line.quantity}</span>
              </label>
              <div className="space-y-2">
                <Label htmlFor={`refund-quantity-${line.id}`}>Refund quantity</Label>
                <Input
                  id={`refund-quantity-${line.id}`}
                  aria-label={`Refund quantity for ${itemName}`}
                  aria-describedby={`refund-quantity-hint-${line.id}`}
                  aria-invalid={checked && !quantityIsValid}
                  inputMode="decimal"
                  maxLength={15}
                  value={quantity}
                  disabled={!checked || isLoading || isPreviewLoading}
                  onChange={(event) => {
                    setQuantities((current) => ({ ...current, [line.id]: event.target.value }));
                    setConfirmationOpen(false);
                    setRefundPreview(null);
                    setIdempotencyKey(null);
                    setErrorMessage(null);
                  }}
                />
                <p id={`refund-quantity-hint-${line.id}`} className={checked && !quantityIsValid ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                  {checked && !quantityIsValid
                    ? `Enter a quantity greater than 0 and no more than ${line.quantity}.`
                    : `Maximum sold quantity: ${line.quantity}.`}
                </p>
              </div>
            </div>;
          })}
        </div>
      )}
      {!confirmationOpen ? <Button type="button" disabled={!selectionIsValid || isLoading || isPreviewLoading} onClick={() => void reviewRefund()}>
        {isPreviewLoading ? 'Loading refund preview…' : 'Review refund'}
      </Button> : (
        <div role="group" className="space-y-3 rounded-md bg-muted/40 p-3" aria-label="Confirm refund">
          <p className="font-medium">Confirm refund for {selectedLines.length} selected line{selectedLines.length === 1 ? '' : 's'}?</p>
          <ul className="list-disc space-y-1 pl-5 text-sm">{validLines.map((line) => <li key={line.id}>{line.productId ?? line.variantId ?? line.id}: quantity {quantities[line.id]}</li>)}</ul>
          {refundPreview && <p className="font-semibold">Refund amount: {formatMinorAmount(refundPreview.amountMinor, refundPreview.currency)}</p>}
          <p className="text-sm text-muted-foreground">Current order status: {orderStatus}. The order service will revalidate refund eligibility before submission.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={isLoading || !refundPreview} onClick={() => void confirmRefund()}>{isLoading ? 'Submitting…' : 'Confirm refund'}</Button>
            <Button type="button" variant="outline" disabled={isLoading} onClick={() => {
              setConfirmationOpen(false);
              setRefundPreview(null);
              setIdempotencyKey(null);
            }}>Edit selection</Button>
          </div>
        </div>
      )}
      {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}
      {successMessage && <p role="status" aria-live="polite" className="text-sm">{successMessage}</p>}
    </section>
  );
}

function isValidRefundQuantity(requested: string, sold: string): boolean {
  const requestedUnits = quantityUnits(requested);
  const soldUnits = quantityUnits(sold);
  return requestedUnits !== null && soldUnits !== null && requestedUnits > 0n && requestedUnits <= soldUnits;
}

function quantityUnits(value: string): bigint | null {
  if (!/^(?:0|[1-9]\d*)(?:\.\d{1,4})?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  return BigInt(whole!) * 10_000n + BigInt(fraction.padEnd(4, '0'));
}

function apiErrorCode(error: unknown): string | undefined {
  const pending: unknown[] = [error];
  const visited = new Set<object>();
  while (pending.length > 0) {
    const value = pending.pop();
    if (!value || typeof value !== 'object' || visited.has(value)) continue;
    visited.add(value);
    const record = value as Record<string, unknown>;
    if (typeof record.code === 'string' && /^[a-z][a-z0-9_]{1,80}$/.test(record.code)) return record.code;
    pending.push(record.data, record.message, record.error);
  }
  return undefined;
}

function RelatedNode({ node }: { node: RelatedOrderNode }) {
  return <li className="py-1"><span className="font-medium">{node.id}</span>{node.status && <span className="ml-2 text-muted-foreground">{node.status}</span>}{node.children.length > 0 && <ul className="ml-5 border-l pl-4">{node.children.map((child) => <RelatedNode key={child.id} node={child} />)}</ul>}</li>;
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 break-words text-sm">{value}</dd></div>;
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatDecimalUnitPrice(value: string, currency: string): string {
  return `${currency} ${value}`;
}
